import { ChangeEvent, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Control, useController } from 'react-hook-form'
import { TextField } from '@mui/material'

import { hcpInputToTenths, isHcpInputAllowed, isHcpTenthsInRange, tenthsToHcpInput } from 'utils/hcpTenths'

interface Props {
  control: Control<any>
  name: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
}

export default function HcpTenthsField(props: Props): JSX.Element {
  const { control, name, placeholder, required, disabled, className } = props
  const { field, fieldState } = useController({ control, name })
  const [input, setInput] = useState(() => tenthsToHcpInput(field.value))

  useEffect(() => {
    setInput((current) => {
      const storedTenths = typeof field.value === 'number' ? field.value : undefined

      return hcpInputToTenths(current) === storedTenths ? current : tenthsToHcpInput(field.value)
    })
  }, [field.value])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextInput = event.target.value

    if (!isHcpInputAllowed(nextInput)) {
      return
    }

    const tenths = hcpInputToTenths(nextInput)

    if (tenths !== undefined && !isHcpTenthsInRange(tenths)) {
      return
    }

    setInput(nextInput)
    field.onChange(tenths ?? null)
  }

  const handleBlur = () => {
    setInput((current) => (hcpInputToTenths(current) === undefined ? '' : current.replace(/\.$/, '')))
    field.onBlur()
  }

  return (
    <StyledTextField
      inputRef={field.ref}
      name={field.name}
      value={input}
      onChange={handleChange}
      onBlur={handleBlur}
      label=""
      variant="outlined"
      type="text"
      inputProps={{ inputMode: 'decimal' }}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      error={!!fieldState.error}
      helperText={fieldState.error?.message}
      className={className}
    />
  )
}

const StyledTextField = styled(TextField)`
  width: 100%;

  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }
`
